<script setup lang="ts">
import { Authenticator, translations } from "@aws-amplify/ui-vue";
import { formFields } from '@/config/AuthFormFields';
import { fetchAuthSession, signUp } from 'aws-amplify/auth';
import { Hub, I18n } from 'aws-amplify/utils';
import { useRouter } from 'vue-router';
import { userStore } from '@/stores/user';
import Card from 'primevue/card';
import logoUrl from '@/assets/1.png';

import { ref, onUnmounted, onMounted, computed, watch } from 'vue';
import TurnstileWidget from '@/components/TurnstileWidget.vue'

import "@aws-amplify/ui-vue/styles.css";

const user = userStore();
const router = useRouter();

const listener = async ({ payload }) => {
  console.log(payload.event)
  switch (payload.event) {
    case 'signedIn':
        await user.refreshUser();
        router.push({ name: 'api-subscriptions' })
        break;
    case 'signedUp':
        await user.refreshUser();
        router.push({ name: 'api-subscriptions' })
        break;
    case 'signedOut':
        router.push({ name: 'home' })
        user.user = null
        break;
    case 'signIn_failure':
        console.error('user sign in failed');
        break;
    case 'signUp_failure':
        console.error('user sign up failed');
        break;
    case 'tokenRefresh_failure':
        console.error('token refresh failed');
        break;
  }
}

const hubListenerCancelToken = Hub.listen('auth', listener)

onUnmounted(() => {
  hubListenerCancelToken();
})

const turnstileToken = ref<string | null>(null)
const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY

// Prepopulate sign-up name/email fields from get params
const signUpParams = new URLSearchParams(window.location.search)
const signUpName = ref(signUpParams.get('name') ?? '')
const signUpEmail = ref(signUpParams.get('email') ?? '')
const signUpPassword = ref('')
const showSignUpPassword = ref(false)

let session = ref(null)
onMounted(async () => {
  session.value = await fetchAuthSession()

  if (user.loggedIn === true) {
    router.push({ name: 'api-subscriptions' })
  }

  ;(window as any).onTurnstileSuccess = (token: string) => { turnstileToken.value = token }
  ;(window as any).onTurnstileExpired = () => { turnstileToken.value = null }
  const script = document.createElement('script')
  script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
  script.async = true
  document.head.appendChild(script)
})

watch(() => user.loggedIn, (newValue) => {
  if (newValue === true) {
    router.push({ name: 'api-subscriptions' })
  }
})


I18n.putVocabularies(translations);
I18n.putVocabulariesForLanguage('en', {
  'Incorrect username or password.': 'Unknown email or password. If this is your first time signing in, please Create a New Account - any existing subscriptions will automatically be linked.',
  'Cannot reset password for the user as there is no registered/verified email or phone_number': 'Email address is unverified. Please complete signup, or contact us at team@the-odds-api.com',
});

const preSignUpErrorPrefix = 'PreSignUp failed with error';

const getFriendlySignUpError = (error: unknown) => {
  if (!(error instanceof Error)) {
    return 'We could not create your account right now. Please try again.';
  }

  if (!error.message.startsWith(preSignUpErrorPrefix)) {
    return error.message;
  }

  const detailedMessage = error.message
    .slice(preSignUpErrorPrefix.length)
    .replace(/^[:\s-]+/, '')
    .trim();

  return detailedMessage || 'We could not create your account right now. Please try again.';
};

// Get from initialAuthState get param
const initialState = computed(() => {
  const params = new URLSearchParams(window.location.search);
  // ensure it's either signIn | signUp | forgotPassword. Fallback to signIn
  const validStates = ['signIn', 'signUp', 'forgotPassword'];
  const state = params.get('initialAuthState');
  if (validStates.includes(state)) {
    return state;
  }
  return 'signIn';
});

const goToHomepage = () => {
  if (typeof window !== 'undefined') {
    window.location.href = 'https://the-odds-api.com';
  }
};

const services = {
  async handleSignUp(input: Parameters<typeof signUp>[0] & { options?: { userAttributes?: Record<string, string>; [k: string]: unknown } }) {
    if (!turnstileToken.value) {
      throw new Error('Please complete the validation challenge before signing up.');
    }

    try {
      return await signUp({
        ...input,
        options: {
          ...input.options,
          clientMetadata: {
            turnstileToken: turnstileToken.value,
          },
          userAttributes: {
            ...input.options?.userAttributes,
          },
        },
      });
    } catch (error) {
      throw new Error(getFriendlySignUpError(error));
    }
  },
};

</script>

<template>

    <img
      :src="logoUrl"
      alt="Logo"
      @click="goToHomepage"
      class="mx-auto mt-2 mb-4 cursor-pointer"
      style="max-width: 10rem;"
      />

    <div v-if="user.loggedIn === false && user.userRefreshing" class="text-center mt-20">
      <p class="text-gray-500">Authenticating...</p>
    </div>

    <authenticator
      :form-fields="formFields"
      :login-mechanisms="['email']"
      :initial-state="initialState"
      :services="services"
      class="mt-20"
    >
      <template v-slot="{ user, signOut }"></template>
      <template #sign-up-fields>
        <div class="amplify-flex" style="flex-direction: column">
          <div class="amplify-flex amplify-field amplify-textfield">
            <label class="amplify-label" for="name">Name</label>
            <input
              id="name"
              name="name"
              type="text"
              class="amplify-input"
              placeholder="Name"
              autocomplete="name"
              required
              v-model="signUpName"
            />
          </div>

          <div class="amplify-flex amplify-field amplify-textfield">
            <label class="amplify-label" for="email">Email - if you have existing subscriptions, use the same email</label>
            <input
              id="email"
              name="email"
              type="email"
              class="amplify-input"
              placeholder="Email"
              autocomplete="email"
              required
              v-model="signUpEmail"
            />
          </div>

          <div class="amplify-flex amplify-field amplify-passwordfield custom-signup-password">
            <label class="amplify-label" for="password">Password - at least 8 characters</label>
            <div class="amplify-flex amplify-field-group amplify-field-group--horizontal">
              <div class="amplify-field-group__field-wrapper">
                <input
                  id="password"
                  name="password"
                  :type="showSignUpPassword ? 'text' : 'password'"
                  class="amplify-input amplify-field-group__control"
                  placeholder="Password"
                  autocomplete="new-password"
                  minlength="8"
                  required
                  v-model="signUpPassword"
                />
              </div>
              <div class="amplify-field-group__outer-end">
                <button
                  type="button"
                  class="amplify-field-group__control amplify-field-group__control--button amplify-button amplify-button--link amplify-field__show-password"
                  :aria-label="showSignUpPassword ? 'Hide password' : 'Show password'"
                  @click="showSignUpPassword = !showSignUpPassword"
                >
                  <i :class="['pi', showSignUpPassword ? 'pi-eye-slash' : 'pi-eye']"></i>
                </button>
              </div>
            </div>
          </div>

          <input type="hidden" name="confirm_password" :value="signUpPassword" />

          <div class="flex justify-center pb-2">
            <TurnstileWidget
              :sitekey="turnstileSiteKey"
              @token="turnstileToken = $event"
            />
          </div>
        </div>
      </template>
      <template #confirm-reset-password-header>
        <p class="text-sm text-left mb-4">
          Not receiving the confirmation code? You might need to
          <a href="https://dash.the-odds-api.com/?initialAuthState=signUp" class="underline text-blue-600 hover:text-blue-800">create a new account</a>
          if you have not already done so. Any active subscriptions will be linked to the new account.
        </p>
      </template>
    </authenticator>

</template>

<style>
.amplify-alert .amplify-alert__dismiss,
.amplify-alert__dismiss[data-amplify-button] {
  /*
    HACK HACK
    Added to prevent dismissing of alerts, such as validation errors 
    Once a user dismissed an error, subsequent errors would not appear, leading to a confusion
  */
  display: none !important;
}

.custom-signup-password .amplify-field-group--horizontal {
  align-items: stretch;
  border: 1px solid var(--amplify-components-fieldcontrol-border-color);
  border-radius: var(--amplify-components-fieldcontrol-border-radius);
  overflow: hidden;
}

.custom-signup-password .amplify-field-group--horizontal:focus-within {
  border-color: var(--amplify-components-fieldcontrol-focus-border-color);
  box-shadow: 0 0 0 1px var(--amplify-components-fieldcontrol-focus-border-color);
}

.custom-signup-password .amplify-field-group__field-wrapper {
  flex: 1;
}

.custom-signup-password .amplify-field-group__field-wrapper .amplify-input {
  border: none;
  border-radius: 0;
}

.custom-signup-password .amplify-field-group__field-wrapper .amplify-input:focus {
  box-shadow: none;
}

.custom-signup-password .amplify-field-group__outer-end .amplify-field__show-password {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 var(--amplify-space-medium);
  border: none;
  border-left: 1px solid var(--amplify-components-fieldcontrol-border-color);
  border-radius: 0;
  background: transparent;
  cursor: pointer;
}
</style>